import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Bot, Play, CalendarClock, RotateCcw, X } from "lucide-react";
import { useCompanyId } from "@/lib/CompanyContext";
import { issuesApi, type Issue } from "@/api/issues";
import { ndAgentsApi } from "@/api/nd-agents";
import { api } from "@/lib/api";
import {
  PageHeader,
  StatusPill,
  RunStatusHero,
  HeartbeatEventLog,
  lastEventLabel,
  type LoggedEvent,
  type RunState,
} from "@/components/ui";

/**
 * Execucao — orquestra a tela de loading da execução de uma task.
 *
 * Páginas finas delegam UI pros primitives:
 *  - PageHeader: header consistente com PageHeader pattern
 *  - RunStatusHero: status + agente + tempo decorrido
 *  - HeartbeatEventLog: timeline de eventos reais do agente
 *
 * Quando issue.status muda pra done/in_review/blocked, redireciona pro
 * Resultado (que cuida de cada caso com ApprovalBar/BlockedBanner).
 */

type HeartbeatRun = {
  id: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  contextSnapshot?: { issueId?: string | null } | null;
};

type LiveWsEvent = {
  payload?: { issueId?: string; runId?: string };
};

export default function Execucao() {
  const { id: issueId } = useParams<{ id: string }>();
  const companyId = useCompanyId();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [, setTick] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);

  const issueQuery = useQuery({
    queryKey: ["issues", issueId, "detail"],
    queryFn: () => issuesApi.detail(issueId!),
    enabled: !!issueId,
    refetchInterval: 3000,
  });

  const runsQuery = useQuery({
    queryKey: ["companies", companyId, "heartbeat-runs", { issueId }],
    queryFn: () =>
      api.get<HeartbeatRun[]>(`/companies/${companyId}/heartbeat-runs?limit=20`),
    enabled: !!companyId && !!issueId,
    refetchInterval: 3000,
  });

  const agentsQuery = useQuery({
    queryKey: ["companies", companyId, "nd-agents"],
    queryFn: () => ndAgentsApi.list(companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  });

  // Inicia uma tarefa que está em backlog (ex: post de calendário ainda não rodou).
  const startMutation = useMutation({
    mutationFn: () => issuesApi.start(companyId!, issueId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", issueId, "detail"] });
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "heartbeat-runs"] });
    },
  });

  // Reinicia a geração de uma tarefa presa carregando (re-dispara o backend).
  const retryMutation = useMutation({
    mutationFn: () => issuesApi.retry(issueId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", issueId, "detail"] });
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "heartbeat-runs"] });
    },
  });

  // Cancela a tarefa (→ "cancelado") e volta pro Quadro.
  const cancelMutation = useMutation({
    mutationFn: () => issuesApi.cancel(issueId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["issues", issueId, "detail"] });
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "issues"] });
      navigate("/quadro");
    },
  });

  const matchingRuns = useMemo(() => {
    const all = runsQuery.data ?? [];
    return all.filter((r) => r.contextSnapshot?.issueId === issueId);
  }, [runsQuery.data, issueId]);
  const activeRun = matchingRuns.find(
    (r) => r.status === "running" || r.status === "queued",
  );
  const currentRun = activeRun ?? matchingRuns[0] ?? null;

  const runEventsQuery = useQuery({
    queryKey: ["heartbeat-runs", currentRun?.id, "events"],
    queryFn: () =>
      api.get<LoggedEvent[]>(
        `/heartbeat-runs/${currentRun!.id}/events?limit=200`,
      ),
    enabled: !!currentRun?.id,
    refetchInterval: activeRun ? 2000 : false,
  });

  // Tick pra re-render do timer
  useEffect(() => {
    const i = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(i);
  }, []);

  // WebSocket invalida queries quando algo acontece
  useEffect(() => {
    if (!companyId) return;
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/api/companies/${companyId}/events/ws`;
    let cancelled = false;
    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (msg) => {
        if (cancelled) return;
        try {
          const ev = JSON.parse(msg.data) as LiveWsEvent;
          const evIssueId = ev.payload?.issueId;
          const evRunId = ev.payload?.runId;
          if (evIssueId !== issueId && (!currentRun || evRunId !== currentRun.id)) return;
          queryClient.invalidateQueries({ queryKey: ["issues", issueId, "detail"] });
          if (currentRun?.id) {
            queryClient.invalidateQueries({
              queryKey: ["heartbeat-runs", currentRun.id, "events"],
            });
          }
        } catch {
          /* ignora */
        }
      };
    } catch {
      /* fallback no polling */
    }
    return () => {
      cancelled = true;
      ws?.close();
      wsRef.current = null;
    };
  }, [companyId, issueId, currentRun?.id, queryClient]);

  if (!issueId) {
    return (
      <div style={{ padding: 32 }} className="wf-hand-note">
        ID da tarefa não fornecido.
      </div>
    );
  }
  if (issueQuery.isLoading) {
    return (
      <div style={{ padding: 32 }} className="wf-hand-note">
        carregando tarefa…
      </div>
    );
  }
  if (issueQuery.isError) {
    return (
      <div style={{ padding: 32, color: "var(--nd-orange-pun)" }}>
        Erro: {(issueQuery.error as Error).message}
      </div>
    );
  }

  const issue = issueQuery.data as Issue;

  // Quando há entregável / em revisão / bloqueada, vai pro Resultado
  if (
    issue.status === "done" ||
    issue.status === "in_review" ||
    issue.status === "blocked"
  ) {
    return <Navigate to={`/resultado/${issueId}`} replace />;
  }

  const assignedAgent =
    agentsQuery.data?.find((a) => a.id === issue.assigneeAgentId) ?? null;

  // Tarefa em backlog que nunca rodou: NÃO mostra tela de loading (não há run).
  // Mostra um estado "ainda não começou" com CTA pra executar.
  if (issue.status === "backlog") {
    const scheduledLabel = issue.scheduledFor
      ? new Date(issue.scheduledFor).toLocaleDateString("pt-BR", {
          day: "2-digit",
          month: "long",
        })
      : null;
    return (
      <div style={{ background: "#fafaf6", minHeight: "100%" }}>
        <PageHeader
          backTo="/quadro"
          backLabel="Quadro"
          icon={Bot}
          kicker={issue.identifier ?? "Tarefa"}
          title={issue.title}
          rightSlot={<StatusPill status={issue.status} />}
          maxWidth={760}
        />
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "20px 24px 64px" }}>
          <div
            className="wf-card"
            style={{
              padding: 28,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 14,
                background: "rgba(255,112,0,0.10)",
                color: "var(--nd-orange)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CalendarClock size={24} strokeWidth={1.75} />
            </div>
            <div className="wf-display" style={{ fontSize: 18, fontWeight: 700 }}>
              Esta tarefa ainda não começou
            </div>
            <div style={{ fontSize: 14, color: "var(--wf-ink-2)", lineHeight: 1.5, maxWidth: 420 }}>
              {scheduledLabel
                ? `Está planejada para ${scheduledLabel}. Você pode executar agora ou deixar para o dia.`
                : "Ela está na fila do Quadro. Execute quando quiser."}
              {assignedAgent ? ` Responsável: ${assignedAgent.name}.` : ""}
            </div>
            <button
              type="button"
              className="wf-btn wf-btn--primary"
              disabled={startMutation.isPending}
              onClick={() => startMutation.mutate()}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, marginTop: 4 }}
            >
              <Play size={15} strokeWidth={2} fill="currentColor" />
              {startMutation.isPending ? "Iniciando…" : "Executar tarefa"}
            </button>
            {startMutation.isError && (
              <div style={{ fontSize: 12, color: "var(--nd-orange-pun)" }}>
                {(startMutation.error as Error).message}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const startISO =
    currentRun?.startedAt ??
    currentRun?.createdAt ??
    issue.startedAt ??
    issue.createdAt;
  const startMs = startISO ? new Date(startISO).getTime() : null;
  const finishMs = currentRun?.finishedAt
    ? new Date(currentRun.finishedAt).getTime()
    : Date.now();
  const elapsedSec =
    startMs !== null ? Math.max(0, Math.floor((finishMs - startMs) / 1000)) : 0;

  // Computa estado (working/done/failed/queued)
  const runState: RunState =
    currentRun?.status === "failed"
      ? "failed"
      : issue.status === "todo" && !currentRun
        ? "queued"
        : issue.status === "in_progress" ||
            currentRun?.status === "running" ||
            currentRun?.status === "queued"
          ? "working"
          : "done";
  const isWorking = runState === "working" || runState === "queued";

  const events = runEventsQuery.data ?? [];

  const headline =
    runState === "queued"
      ? "Aguardando agente acordar"
      : runState === "working"
        ? "Trabalhando agora"
        : runState === "failed"
          ? "Run falhou"
          : "Concluiu o run";
  const subline = lastEventLabel(events);

  return (
    <div style={{ background: "#fafaf6", minHeight: "100%" }}>
      <PageHeader
        backTo="/quadro"
        backLabel="Quadro"
        icon={Bot}
        kicker={issue.identifier ?? "Tarefa"}
        title={issue.title}
        rightSlot={<StatusPill status={issue.status} />}
        maxWidth={760}
      />

      <div
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "20px 24px 64px",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <RunStatusHero
          state={runState}
          agentName={assignedAgent?.name}
          headline={headline}
          subline={subline}
          elapsedSec={elapsedSec}
        />

        {/* Travou carregando? Reinicia a geração ou cancela. (Se ficar >15min
            parada, o sistema move sozinho pra "bloqueado" por timeout.) */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            className="wf-btn wf-btn--primary"
            disabled={retryMutation.isPending}
            onClick={() => retryMutation.mutate()}
            style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
          >
            <RotateCcw size={15} strokeWidth={2} />
            {retryMutation.isPending ? "Reiniciando…" : "Reiniciar geração"}
          </button>
          <button
            type="button"
            className="wf-btn"
            disabled={cancelMutation.isPending}
            onClick={() => {
              if (window.confirm("Cancelar esta tarefa? Ela não será mais gerada.")) {
                cancelMutation.mutate();
              }
            }}
            style={{ display: "inline-flex", alignItems: "center", gap: 7 }}
          >
            <X size={15} strokeWidth={2} />
            {cancelMutation.isPending ? "Cancelando…" : "Cancelar tarefa"}
          </button>
          <span style={{ fontSize: 12, color: "var(--wf-ink-3)" }}>
            Demorando demais? Reinicie ou cancele.
          </span>
        </div>
        {(retryMutation.isError || cancelMutation.isError) && (
          <div style={{ fontSize: 12, color: "var(--nd-orange-pun)" }}>
            {((retryMutation.error ?? cancelMutation.error) as Error)?.message}
          </div>
        )}

        <HeartbeatEventLog events={events} isWorking={isWorking} />
      </div>
    </div>
  );
}
